import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { CurveType } from "recharts/types/shape/Curve";
import type { AxisDomain, BaseAxisProps } from "recharts/types/util/types";
import { CHART_HEIGHT, CHART_MARGIN } from "./constants";
import { EmptyState } from "./empty-state";
import { MetricTooltip } from "./metric-tooltip";
import { formatDateToUTC } from "./util";

export interface ChartDatum {
  x: Date;
  y: number;
}

export function Chart({
  data,
  displayEmptyState,
  error,
  loading,
  noDataReason,
  syncId,
  type = "linear",
  unit,
  yDomain,
  yTickFormatter,
}: {
  data: ChartDatum[];
  displayEmptyState: boolean;
  error: boolean;
  loading: boolean;
  noDataReason: string;
  syncId: string;
  type?: CurveType;
  unit?: string;
  yDomain?: AxisDomain;
  yTickFormatter?: BaseAxisProps["tickFormatter"];
}) {
  if (error) {
    return <EmptyState error noDataReason={noDataReason} />;
  }

  if (displayEmptyState) {
    return <EmptyState loading={loading} noDataReason={noDataReason} />;
  }

  return (
    <div className="relative" style={{ height: CHART_HEIGHT }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} syncId={syncId} margin={CHART_MARGIN}>
          <defs>
            <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--gray-10)" stopOpacity={0.8} />
              <stop offset="100%" stopColor="var(--gray-4)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="x"
            interval="equidistantPreserveStart"
            className="text-xs font-sans"
            tickFormatter={(tick: Date) => formatDateToUTC(tick) ?? ""}
            tickMargin={8}
          />
          <YAxis
            domain={yDomain}
            allowDecimals={false}
            dataKey="y"
            tickFormatter={yTickFormatter}
            tickLine={false}
            tickMargin={0}
            style={{
              fontSize: "0.7rem",
              fontFamily: "jetbrains-mono",
            }}
            width={32}
          />
          <Tooltip
            content={(props) => <MetricTooltip {...props} unit={unit} />}
            isAnimationActive={false}
            cursor={{
              stroke: "var(--gray-12)",
            }}
          />
          <Area
            isAnimationActive={false}
            dot={false}
            type={type}
            activeDot={{
              fill: "var(--gray-12)",
              fillOpacity: 1,
              stroke: "0",
            }}
            dataKey="y"
            fill="url(#gradient)"
            stroke="#000"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
